import { useEffect, useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import type { LucideIcon } from 'lucide-react'
import {
  LayoutDashboard, MapPin, CheckSquare, Users, Car, School, FileText,
  Percent, Wallet, UserX, Headphones, ClipboardList, Image, Bell,
  Package, Tag, Gift, Calendar, CalendarClock, Star, BarChart2, CreditCard, Shield,
  Settings, ChevronRight, BadgeCheck, LayoutList, UserMinus, MessageSquare, PiggyBank, Banknote, Coins, Layers, History
} from 'lucide-react'
import { cn } from '@/shared/utils'
import { useUIStore } from '@/store/useUIStore'
import { useBookingsSeenStore } from '@/store/useBookingsSeenStore'
import { usePendingBookingsCount, useAutoReassignIssuesCount } from '@/api/hooks'
import type { NavSection, NavItem } from './navConfig'

// Real, already-tracked counts surfaced as a small bell badge next to the
// sidebar items they belong to — not a guess at categorising generic push
// notifications, but the actual unread-tickets / pending-bookings numbers.
function NavBadge({ count }: { count: number }) {
  if (count <= 0) return null
  return (
    <span className="flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-red-500 text-white text-[10px] font-bold leading-none flex-shrink-0">
      <Bell size={9} />
      {count > 99 ? '99+' : count}
    </span>
  )
}

// Below the `lg` breakpoint the sidebar is an off-canvas drawer, so a leaf
// link navigating should also close it — otherwise it stays open over the page.
function closeOnMobileNavigate(setSidebarOpen: (v: boolean) => void) {
  if (typeof window !== 'undefined' && window.innerWidth < 1024) setSidebarOpen(false)
}

const ICON_MAP: Record<string, LucideIcon> = {
  LayoutDashboard, MapPin, CheckSquare, Users, Car, School, FileText,
  Percent, Wallet, UserX, HeadphonesIcon: Headphones, ClipboardList,
  Image, Bell, Package, Tag, Gift, Calendar, CalendarClock, Star, BarChart2,
  CreditCard, Shield, Settings, BadgeCheck, LayoutList, UserMinus, MessageSquare, PiggyBank, Banknote, Coins, Layers, History,
}

function NavIcon({ name, size = 15 }: { name: string; size?: number }) {
  const Icon = ICON_MAP[name]
  if (!Icon) return <span style={{ width: size, height: size, display: 'inline-block' }} />
  return <Icon size={size} />
}

function NavItemRow({ item, depth = 0, badgeCounts }: { item: NavItem; depth?: number; badgeCounts?: Record<string, number> }) {
  const location = useLocation()
  const { t } = useTranslation()
  const setSidebarOpen = useUIStore(s => s.setSidebarOpen)
  const label = t(`navLabels.${item.label}`, item.label)
  const hasActiveChild = item.children?.some(c => location.pathname.startsWith(c.path)) ?? false
  const isActive = location.pathname === item.path || hasActiveChild
  const [open, setOpen] = useState(isActive)
  const pl = depth === 0 ? 14 : 14 + depth * 14
  const badge = badgeCounts?.[item.label] ?? 0

  if (item.children?.length) {
    return (
      <div>
        <button
          onClick={() => setOpen(o => !o)}
          style={{ paddingInlineStart: pl }}
          className={cn(
            'w-full flex items-center gap-2.5 pe-3.5 py-2 text-[14px] transition-colors border-s-[3px]',
            isActive
              ? 'bg-brand-50 border-brand-500 text-brand-600 font-semibold'
              : 'border-transparent text-gray-600 hover:bg-gray-50 hover:text-gray-900'
          )}
        >
          {depth === 0 && <NavIcon name={item.icon} />}
          <span className="flex-1 text-start">{label}</span>
          <NavBadge count={badge} />
          <ChevronRight size={12} className={cn('text-gray-400 transition-transform flex-shrink-0 rtl:rotate-180', open && 'rotate-90 rtl:-rotate-90')} />
        </button>
        {open && (
          <div className="bg-gray-50/60">
            {item.children.map(child => <NavItemRow key={child.path} item={child} depth={depth + 1} badgeCounts={badgeCounts} />)}
          </div>
        )}
      </div>
    )
  }

  return (
    <NavLink
      to={item.path}
      onClick={() => closeOnMobileNavigate(setSidebarOpen)}
      style={{ paddingInlineStart: pl }}
      className={({ isActive: ia }) => cn(
        'flex items-center gap-2.5 pe-3.5 py-2 text-[14px] transition-colors border-s-[3px]',
        ia
          ? 'bg-brand-50 border-brand-500 text-brand-600 font-semibold'
          : 'border-transparent text-gray-600 hover:bg-gray-50 hover:text-gray-900'
      )}
    >
      {depth === 0 && <NavIcon name={item.icon} />}
      <span className="flex-1">{label}</span>
      <NavBadge count={badge} />
    </NavLink>
  )
}

interface Props { sections: NavSection[]; open: boolean }

export function Sidebar({ sections, open }: Props) {
  const { t } = useTranslation()
  const setSidebarOpen = useUIStore(s => s.setSidebarOpen)

  // Sidebar badge counts (pending bookings, open reassignment issues) are
  // switched off for now — both polled every 60s on every page, which was
  // more network chatter than wanted. Re-enable by passing `true` (and scoping
  // out school admins again, e.g. via useAuthStore's user_type) to resume.
  const { data: pendingTotal } = usePendingBookingsCount(false)
  const seenPendingCount = useBookingsSeenStore(s => s.seenPendingCount)
  const { data: openIssuesCount } = useAutoReassignIssuesCount(false)
  const badgeCounts: Record<string, number> = {
    Bookings: Math.max(0, (pendingTotal ?? 0) - seenPendingCount),
    'Reassignment Issues': openIssuesCount ?? 0,
  }

  // Skip the slide transition on the very first paint — otherwise the browser
  // can animate from the implicit pre-mount transform straight into the
  // correct closed position, showing a brief flash of the drawer opening.
  const [readyForTransition, setReadyForTransition] = useState(false)
  useEffect(() => {
    const id = requestAnimationFrame(() => setReadyForTransition(true))
    return () => cancelAnimationFrame(id)
  }, [])

  // sidebarOpen's initial value is only computed once, from the window width
  // at first page load. If the viewport later crosses the lg breakpoint
  // without a reload — resizing the window, or toggling a DevTools device
  // emulator — that stale value would otherwise leave the mobile drawer stuck
  // open (covering the screen) or the desktop rail stuck collapsed. Syncing
  // here keeps it matching reality: closed drawer on mobile, expanded rail on
  // desktop, whenever that boundary is actually crossed.
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)')
    const onChange = (e: MediaQueryListEvent) => setSidebarOpen(e.matches)
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [setSidebarOpen])
  return (
    <>
      {/* Backdrop — mobile/tablet only, closes the drawer on tap outside it */}
      {open && (
        <div
          className="fixed inset-0 bg-black/40 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}
      <aside className={cn(
        'flex flex-col bg-white border-e border-gray-100 h-full flex-shrink-0 z-40',
        'fixed inset-y-0 start-0 w-[270px]',
        readyForTransition && 'transition-transform duration-200',
        open ? 'translate-x-0' : '-translate-x-full rtl:translate-x-full',
        'lg:static lg:inset-auto lg:z-auto lg:translate-x-0 lg:overflow-hidden',
        readyForTransition && 'lg:transition-[width] lg:duration-200',
        open ? 'lg:w-[270px]' : 'lg:w-0'
      )}>
        {/* Logo */}
        <div className="flex items-center justify-center px-4 py-5 border-b border-gray-100 flex-shrink-0">
          <img
            src="/logo.svg"
            alt="Drivys"
            style={{ height: 60, width: 'auto', objectFit: 'contain', display: 'block' }}
            onError={e => {
              const img = e.currentTarget as HTMLImageElement
              img.style.display = 'none'
              const span = document.createElement('span')
              span.style.cssText = 'font-weight:900;font-size:22px;color:#f68b2c;letter-spacing:3px'
              span.textContent = 'DRIVYS'
              img.parentElement?.appendChild(span)
            }}
          />
        </div>

        <nav className="flex-1 overflow-y-auto py-2">
          {sections.map((sec, i) => (
            <div key={sec.section} className={cn(i > 0 && 'mt-1 pt-1 border-t border-gray-100')}>
              <div className="px-4 pt-3 pb-1.5 text-[12px] font-extrabold text-gray-400 tracking-[0.12em] uppercase">{t(`navSections.${sec.section}`, sec.section)}</div>
              {sec.items.map((item, j) => <NavItemRow key={`${item.path}-${j}`} item={item} badgeCounts={badgeCounts} />)}
            </div>
          ))}
        </nav>
      </aside>
    </>
  )
}
